<script setup lang="ts">
  /**
   * Form field — 03-components/15-form-inputs · OSCloud DS 1.1.154
   */
  import { computed, onMounted, ref, watch } from 'vue'

  import { useInputMask, type InputMaskState } from '@/composable/useInputMask'

  const props = withDefaults(
    defineProps<{
      modelValue?: string
      label?: string
      /** Sentence-case label variant for dense forms and consent copy. */
      plainLabel?: boolean
      type?: string
      placeholder?: string
      /** Neutral message under the control. */
      hint?: string
      error?: string
      /**
       * Paints the control invalid when the message lives elsewhere — a password pair's match
       * line, pointed at with aria-describedby. `error` sets the same state with its own message.
       */
      invalid?: boolean
      disabled?: boolean
      /** Tabular numerals, for amounts and card numbers. */
      numeric?: boolean
      /**
       * Static unit or currency prefix inside the control — "EUR", "kr", "%". Name the unit in
       * the input's label or aria-label as well: the affix itself is aria-hidden and must not be
       * read twice.
       */
      affix?: string
      fullWidth?: boolean
      /** 'md' (default 44px) or 'lg' (52px, used on prominent forms like login). */
      size?: 'md' | 'lg'
      autocomplete?: string
      id?: string
      /**
       * Input mask: a pattern (`#` digit, `A` letter, `*` either, `\x` literal) or a preset — `card`, `expiry`,
       * `personnummer`, `orgnr`, `postcode`. Formats only on type text, tel and search. State the format in `hint`
       * too — the placeholder is gone at the first keystroke.
       */
      mask?: string
      /** On blur, an incomplete non-empty masked value sets aria-invalid; completing it clears it at once. */
      maskValidate?: boolean
      /** Renders a hidden input with this name carrying the unformatted value for the server. */
      maskRawName?: string
    }>(),
    {
      modelValue: '',
      label: '',
      plainLabel: false,
      type: 'text',
      placeholder: '',
      hint: '',
      error: '',
      invalid: false,
      disabled: false,
      numeric: false,
      affix: '',
      fullWidth: false,
      size: 'md',
      autocomplete: undefined,
      id: undefined,
      mask: '',
      maskValidate: false,
      maskRawName: '',
    },
  )

  const emit = defineEmits<{ 'update:modelValue': [value: string]; 'mask-change': [state: InputMaskState] }>()

  // Forward fall-through attrs (aria-describedby, autofocus, …) onto the input
  // rather than the wrapper, so they associate with the control itself.
  defineOptions({ inheritAttrs: false })

  const reveal = ref(false)
  const isPassword = computed(() => props.type === 'password')
  // Once revealed, swap to a text input so the value shows.
  const resolvedType = computed(() => (isPassword.value && reveal.value ? 'text' : props.type))

  const inputRef = ref<HTMLInputElement | null>(null)
  let lastMaskedValue: string | undefined
  const inputMask = useInputMask({
    input: inputRef,
    mask: () => props.mask,
    validate: () => props.maskValidate,
    onChange: state => {
      lastMaskedValue = state.value
      if (state.value !== props.modelValue) emit('update:modelValue', state.value)
      emit('mask-change', state)
    },
  })
  const ariaInvalid = computed(() => (props.error || props.invalid || inputMask.invalid.value ? 'true' : 'false'))

  function onInput(event: Event) {
    if (props.mask) inputMask.onInput(event)
    else emit('update:modelValue', (event.target as HTMLInputElement).value)
  }

  onMounted(inputMask.refresh)
  watch(
    () => props.modelValue,
    value => {
      if (props.mask && value !== lastMaskedValue) inputMask.refresh()
    },
    { flush: 'post' },
  )
</script>

<template>
  <!-- Invalid state lives only on the input's aria-invalid; the default height needs no size modifier. -->
  <div class="form-field" :class="{ 'form-field--large': size === 'lg', 'form-field--full-width': fullWidth }">
    <label
      v-if="label || $slots.label"
      :for="id"
      class="form-field__label"
      :class="{ 'form-field__label--plain': plainLabel }"
    >
      <slot name="label">{{ label }}</slot>
    </label>
    <div class="form-field__control">
      <span v-if="affix" class="form-field__affix" aria-hidden="true">{{ affix }}</span>
      <input
        :id="id"
        ref="inputRef"
        v-bind="$attrs"
        class="form-field__input"
        :class="{
          'form-field__input--toggle': isPassword,
          'form-field__input--numeric': numeric,
          'form-field__input--with-affix': Boolean(affix),
        }"
        :type="resolvedType"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :autocomplete="autocomplete"
        :aria-invalid="ariaInvalid"
        :data-raw="mask ? inputMask.raw.value : undefined"
        :data-mask-complete="mask ? String(inputMask.complete.value) : undefined"
        @beforeinput="inputMask.onBeforeInput"
        @input="onInput"
        @compositionend="inputMask.onInput"
        @change="inputMask.refresh"
        @focus="inputMask.onFocus"
        @blur="inputMask.onBlur"
      />
      <input v-if="mask && maskRawName" type="hidden" :name="maskRawName" :value="inputMask.raw.value" />
      <button
        v-if="isPassword"
        type="button"
        class="form-field__toggle"
        :aria-label="reveal ? 'Hide password' : 'Show password'"
        :aria-pressed="reveal"
        @click="reveal = !reveal"
      >
        <!-- eye-off when revealed, eye when hidden -->
        <svg
          v-if="reveal"
          class="form-field__eye-off"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="square"
          aria-hidden="true"
        >
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
        <svg
          v-else
          class="form-field__eye"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="square"
          aria-hidden="true"
        >
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </button>
    </div>
    <span
      v-if="error || hint"
      class="form-field__message"
      :role="error ? 'alert' : undefined"
      :data-test-id="error ? 'field-error' : undefined"
    >
      {{ error || hint }}
    </span>
  </div>
</template>

<style scoped lang="scss">
  /* The foundation every form control composes. It owns label typography and the
     label→control gap, the control surface, hairline, radius and height, hover,
     focus ring, aria-invalid, disabled, placeholder colour and the message line.
     Every value is a --bf-form-* token; a control never re-declares any of it. */
  .form-field {
    display: flex;
    flex-direction: column;
    min-width: 0;

    &--full-width {
      width: 100%;
    }

    /* Shared field-label metrics — kept identical in BfSelect so mixed rows line up. */
    &__label {
      font-family: var(--bf-font-display);
      font-size: 11px;
      font-weight: var(--bf-fw-bold);
      color: var(--bf-off-black);
      text-transform: uppercase;
      letter-spacing: var(--bf-tr-eyebrow);
    }

    /* The gap belongs to the label→control relationship, which exists only when
       the label sits above the control as the field's direct child. */
    > .form-field__label {
      margin-bottom: var(--bf-form-label-gap);
    }

    &__label--plain {
      font-size: var(--bf-fs-xs);
      font-weight: var(--bf-fw-semibold);
      line-height: var(--bf-lh-normal);
      color: var(--bf-fg-1);
      text-transform: none;
      letter-spacing: var(--bf-tr-normal);
    }

    &__control {
      position: relative;
    }

    /* 44px by default. Block padding is zero so the height is the height. */
    &__input {
      width: 100%;
      font-family: var(--bf-font-body);
      font-size: 15px;
      height: var(--bf-form-control-height);
      padding: 0 14px;
      color: var(--bf-fg-1);
      background: var(--bf-form-control-surface);
      border: 1px solid var(--bf-form-control-hairline);
      border-radius: var(--bf-form-control-radius);
      transition:
        border-color var(--bf-dur-fast) var(--bf-ease),
        box-shadow var(--bf-dur-fast) var(--bf-ease),
        background var(--bf-dur-fast) var(--bf-ease);

      /* A date value is laid out by WebKit's shadow pseudo-elements, not by the input's box,
         so the height and padding above do not position it. Deliberately no `appearance: none`
         (it would take Chrome's native picker glyph with it) and no picker-indicator styling. */
      &[type='date'] {
        text-align: left;
        line-height: normal;

        &::-webkit-datetime-edit {
          display: flex;
          align-items: center;
          padding: 0;
          line-height: normal;
        }

        &::-webkit-date-and-time-value {
          text-align: left;
          margin: 0;
          min-height: 0;
          line-height: normal;
        }
      }

      &::placeholder {
        color: var(--bf-form-control-placeholder);
      }

      &:hover:not(:focus):not(:disabled) {
        border-color: var(--bf-form-control-hairline-hover);
      }

      /* One focus ring for every control. aria-expanded="true" is the open
         combobox, which keeps its ring while the pointer is in the list. */
      &:focus-visible,
      &[aria-expanded='true'] {
        outline: none;
        border-color: var(--bf-form-control-hairline-focus);
        box-shadow: var(--bf-form-focus-ring);
        background: var(--bf-form-control-surface-focus);
      }

      &:disabled {
        opacity: var(--bf-form-disabled-opacity);
        cursor: not-allowed;
      }

      /* Invalid is aria-invalid="true" on the control, never a modifier. */
      &[aria-invalid='true'] {
        border-color: var(--bf-form-invalid-hairline);
        background: var(--bf-form-invalid-surface);
      }

      &--numeric {
        font-variant-numeric: tabular-nums;
      }

      &--toggle {
        padding-right: 48px;
      }

      &--with-affix {
        padding-left: 58px;
      }
    }

    /* Mirrors the reveal toggle on the leading side: an element in the slot and a
       modifier on the input that clears it. Inert so a click on it lands in the
       input. Type is the field label's eyebrow recipe stepped to the input's own
       size, in secondary text so it reads as part of the control, never as the
       value. */
    &__affix {
      position: absolute;
      left: 14px;
      top: 50%;
      transform: translateY(-50%);
      pointer-events: none;
      font-family: var(--bf-font-display);
      font-size: var(--bf-fs-sm);
      font-weight: var(--bf-fw-semibold);
      letter-spacing: var(--bf-tr-caps-sm);
      text-transform: uppercase;
      color: var(--bf-fg-2);
    }

    /* Two sizes: the default above and --large, on the wrapper, applied to
       whatever sits in the slot — the select's control included. */
    &--large &__input {
      height: var(--bf-form-control-height-large);
      font-size: var(--bf-fs-base);
    }

    /* The affix steps with the wrapper size, as the input's font does. */
    &--large &__affix {
      font-size: var(--bf-fs-base);
    }

    &--large &__input--with-affix {
      padding-left: 62px;
    }

    &__toggle {
      position: absolute;
      right: 10px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      cursor: pointer;
      padding: 6px;
      color: var(--bf-dusky-1);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: var(--bf-r-md);
      transition:
        color var(--bf-dur-fast) var(--bf-ease),
        background var(--bf-dur-fast) var(--bf-ease);

      &:hover {
        color: var(--bf-orange-dark);
        background: var(--bf-orange-ultralight);
      }
    }

    /* Hint or error line. Neutral by default; red only when it carries
       role="alert" or any control in the field is aria-invalid="true". */
    &__message {
      color: var(--bf-fg-2);
      /* Stated, not inherited: the app's body element still carries the legacy family, so a
         message that leaves this open reads in a different face from the control above it
         and from BfUploadField's own constraint line. */
      font-family: var(--bf-font-body);
      font-size: var(--bf-fs-xs);
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: var(--bf-form-message-gap);
    }

    &__message[role='alert'],
    &:has([aria-invalid='true']) &__message {
      color: var(--bf-form-invalid-text);
    }
  }
</style>
